import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, SvgIcon, useTheme, Tooltip } from "@mui/material";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { getRegularDate } from "utils/time.util";
import {
  setCollectionId,
  setCollectionSourceId,
  setIsCitationAvailable,
  setIsSidebarOpen,
  setIsSummaryAvailable,
  setLevel,
  setReadingLevelAvailable,
  setSelectedBookmarkOption,
  setSummaryInfoData,
} from "context/features/dataSlice";

import { ReactComponent as summarizerLogo } from "assets/icons/summarizer-icon.svg";
import { ReactComponent as citationLogo } from "assets/icons/citation-generator-icon.svg";
import { ReactComponent as readingLogo } from "assets/icons/reading-level-icon.svg";

import { useDispatch, useSelector } from "react-redux";
import { writeStorage, useLocalStorage } from "@rehooks/local-storage";
import ColumnsSection from "./ColumnSection";
import { useColumns } from "features/SourceSearch/components/generate-columns";
import CollectionTableComponent from "features/SourceSearch/components/collection-table-component";
import { defaultVisibilityModel, recentColumns } from "../data/columns";

export default function RecentDataGrid({ recentCollectionSource, loading, filter }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const apiRef = useGridApiRef();

  const { user } = useSelector((state) => state.auth);

  const [storedVisibilityModel, setVisibilityModel] = useLocalStorage(
    `sooth-api-recent-columnvisibility-v2-${user.userId}`,
    defaultVisibilityModel
  );

  const visibilityModel = {
    ...defaultVisibilityModel,
    ...storedVisibilityModel,
  };

  const noRowsMessage = filter
    ? "No bookmarks found matching your search."
    : "It looks like you don't have any bookmarks yet!";

  useEffect(() => {
    if (!visibilityModel || Object.keys(visibilityModel).length === 0) {
      setVisibilityModel(defaultVisibilityModel);
      writeStorage(
        `sooth-api-recent-columnvisibility-v2-${user.userId}`,
        defaultVisibilityModel
      );
      apiRef.current.setColumnVisibilityModel(defaultVisibilityModel);
    } else {
      apiRef.current.setColumnVisibilityModel(visibilityModel);
    }
  }, []);

  const [persistedWidths, setPersistedWidths] = useLocalStorage(
    `sooth-api-recent-columnWidths-${user.userId}`,
    {}
  );

  const [commonWidth, setCommonWidth] = useState(null);

  const columnWidthsRef = useRef(persistedWidths);
  const containerRef = useRef(null);

  const computeCommonWidth = () => {
    const element = containerRef.current;
    let width = element ? element.clientWidth : 0;

    const visibleFields = Object.keys(visibilityModel).filter(
      (field) => visibilityModel[field]
    );
    const unsetCount = visibleFields.length;
    const newCommonWidth = unsetCount > 0 ? Math.floor(width / unsetCount) : 0;
    setCommonWidth(newCommonWidth);
  };

  const debouncedUpdateColumnWidths = () => {
    writeStorage(`sooth-api-recent-columnWidths-${user.userId}`, columnWidthsRef.current);
    setPersistedWidths(columnWidthsRef.current);
    computeCommonWidth();
  };

  const handleColumnResize = (params) => {
    columnWidthsRef.current = {
      ...columnWidthsRef.current,
      [params.colDef.field]: params.width,
    };
    debouncedUpdateColumnWidths();
  };

  useEffect(() => {
    if (Object.keys(persistedWidths).length === 0) {
      setCommonWidth(null);
    }
  }, [persistedWidths]);

  const handleVisibilityChange = (newVisibilityModel) => {
    const updatedVisibility = {
      ...visibilityModel,
      ...newVisibilityModel,
    };
    setVisibilityModel(updatedVisibility);
    writeStorage(`sooth-api-recent-columnvisibility-v2-${user.userId}`, updatedVisibility);
  };

  const handleSidebarOpen = (item, option) => {
    dispatch(setLevel(""));
    dispatch(setSelectedBookmarkOption(option));
    dispatch(setReadingLevelAvailable(item.isReadingLevelAvailable_ysn));
    dispatch(
      setSummaryInfoData({
        publisher: item.sourceName_chr,
        title: item.title_chr,
        published_date: item.sourcePublishedDateTime_dtm,
        url: item.sourceUrl_chr,
        utc_date_published: item.sourcePublishedDateTime_dtm
      })
    );
    dispatch(setCollectionSourceId(item.collectionSourceID_ids));
    dispatch(setCollectionId(item.collectionID_lng));
    dispatch(setIsCitationAvailable(item.isCitationAvailable_ysn));
    dispatch(setIsSummaryAvailable(item.isSummaryAvailable_ysn));
    dispatch(setIsSidebarOpen(true));
  };

  const columns = useColumns({
    fields: recentColumns,
    persistedWidths,
    commonWidth,
    theme,
    handlers: { handleSidebarOpen },
  });



  const scrollToLeft = () => {
    const virtualScroller = document.querySelector(
      ".MuiDataGrid-virtualScroller"
    );
    if (virtualScroller) {
      virtualScroller.scrollLeft = 0;
    }
  };

  const clearWidthModel = () => {
    scrollToLeft();
    writeStorage(`sooth-api-recent-columnWidths-${user.userId}`, {});
    setCommonWidth(null);
    setPersistedWidths({});
    columnWidthsRef.current = {};
  };

  return (
    <Box ref={containerRef} sx={{ width: "100%" }}>
      <ColumnsSection
        visibilityModel={visibilityModel}
        setVisibilityModel={setVisibilityModel}
        columns={columns}
        label={`sooth-api-recent-columnvisibility-v2-${user.userId}`}
        clearWidthModel={clearWidthModel}
        widthModal={persistedWidths}
      />
      <CollectionTableComponent
        apiRef={apiRef}
        rows={recentCollectionSource}
        columns={columns}
        handleColumnResize={handleColumnResize}
        visibilityModel={visibilityModel}
        handleVisibilityChange={handleVisibilityChange}
        getRowId={(row) => row.collectionSourceID_ids}
        showCustomEmptyMessage={true}
        getRowClassName={({ row }) =>
          row.isMisinformationSource_ysn ? "sooth-misinformation-row" : ""
        }
        loading={loading}
        noRowsMessage={noRowsMessage}
      />
    </Box>
  );
}
